/**
 * Malha ponto-a-ponto de voz e video do LFRCORD.
 *
 * Cada participante mantem uma `RTCPeerConnection` por parceiro, com quatro
 * transceivers em ordem fixa — `[0] microfone`, `[1] camera`, `[2] tela`,
 * `[3] audio da tela`. A ordem e o contrato: o indice em `getTransceivers()`
 * diz de que tipo e a trilha remota, e ligar/desligar camera ou transmissao
 * vira `replaceTrack` no sender correspondente, sem renegociar nada.
 */

import {
  DEFAULT_ICE_SERVERS,
  OPUS_MIC_BITRATE,
  OPUS_SHARE_BITRATE,
  type ContentHintMode,
  type QualityId,
  type QualityPreset,
} from "@shared/protocol";
import type { RtcSignal } from "@shared/types";
import type { LfrSocket } from "@/net/socket";
import { SpeakingDetector } from "@/media/audio";
import {
  applyVideoQuality,
  getCamStream,
  getMicStream,
  getScreenStream,
  presetOf,
  stopStream,
  type MicCapture,
} from "@/media/devices";
import { preferCodecs, tuneSdp, type SdpTuning } from "@/media/sdp";
import type {
  CaptureResult,
  MediaKind,
  MeshCallbacks,
  MeshOptions,
  PeerStats,
  VoiceMeshApi,
} from "@/media/types";

const SLOT_MIC = 0;
const SLOT_CAM = 1;
const SLOT_SCREEN = 2;
const SLOT_SCREEN_AUDIO = 3;

const STATS_INTERVAL_MS = 2_000;
const REVIVE_DELAY_MS = 900;
const MAX_EARLY_CANDIDATES = 40;

const RECEIVE_SLOTS: [MediaKind, number][] = [
  ["mic", SLOT_MIC],
  ["cam", SLOT_CAM],
  ["screen", SLOT_SCREEN],
];

type StatRecord = Record<string, unknown>;
type Sample = { bytes: number; at: number };

type RemoteTracks = {
  mic: MediaStreamTrack | null;
  cam: MediaStreamTrack | null;
  screen: MediaStreamTrack | null;
  screenAudio: MediaStreamTrack | null;
};

type RemoteStreams = {
  mic: MediaStream | null;
  cam: MediaStream | null;
  screen: MediaStream | null;
};

type Peer = {
  id: string;
  pc: RTCPeerConnection;
  transceivers: RTCRtpTransceiver[];
  /** Educado = quem tem o menor peerId; ele cede na colisao de ofertas. */
  polite: boolean;
  makingOffer: boolean;
  ignoreOffer: boolean;
  iceRestarted: boolean;
  reviving: boolean;
  tracks: RemoteTracks;
  streams: RemoteStreams;
  pending: RTCIceCandidateInit[];
  samples: Map<string, Sample>;
};

export class VoiceMesh implements VoiceMeshApi {
  private readonly socket: LfrSocket;
  private readonly selfPeerId: string;
  private readonly iceServers: RTCIceServer[];
  private readonly callbacks: MeshCallbacks;
  private readonly detector: SpeakingDetector;
  private readonly peers = new Map<string, Peer>();
  private readonly early = new Map<string, RTCIceCandidateInit[]>();
  private readonly statsTimer: ReturnType<typeof setInterval>;

  private wanted = new Set<string>();
  private shareQuality: QualityId;
  private camQuality: QualityId;
  private contentHint: ContentHintMode;
  private noiseSuppression: boolean;
  private muted = false;
  private destroyed = false;

  private micStream: MediaStream | null = null;
  private micTrack: MediaStreamTrack | null = null;
  /** Desliga o grafo de audio (worklet + AudioContext) por tras da trilha atual, se houver. */
  private micDispose: (() => void) | null = null;
  private camStream: MediaStream | null = null;
  private camTrack: MediaStreamTrack | null = null;
  private screenStream: MediaStream | null = null;
  private screenTrack: MediaStreamTrack | null = null;
  private screenAudioTrack: MediaStreamTrack | null = null;
  private micRequest: Promise<void> | null = null;

  constructor(opts: MeshOptions) {
    this.socket = opts.socket;
    this.selfPeerId = opts.selfPeerId;
    this.callbacks = opts.callbacks;
    this.shareQuality = opts.shareQuality;
    this.camQuality = opts.camQuality;
    this.contentHint = opts.contentHint;
    this.noiseSuppression = opts.noiseSuppression;

    const servers = opts.iceServers.length > 0 ? opts.iceServers : DEFAULT_ICE_SERVERS;
    this.iceServers = servers.map(server => ({
      urls: server.urls,
      username: server.username,
      credential: server.credential,
    }));

    this.detector = new SpeakingDetector((id, speaking) => {
      if (!this.destroyed) this.callbacks.onSpeaking(id, speaking);
    });

    this.statsTimer = setInterval(() => void this.collectStats(), STATS_INTERVAL_MS);
  }

  /* ------------------------------------------------------------------ */
  /* Conjunto de conexoes                                                */
  /* ------------------------------------------------------------------ */

  syncPeers(peerIds: string[]): void {
    if (this.destroyed) return;
    this.wanted = new Set(peerIds.filter(id => id !== this.selfPeerId));
    for (const id of [...this.peers.keys()]) {
      if (!this.wanted.has(id)) this.dropPeer(id);
    }
    for (const id of [...this.early.keys()]) {
      if (!this.wanted.has(id)) this.early.delete(id);
    }
    for (const id of this.wanted) {
      if (!this.peers.has(id)) this.createPeer(id);
    }
  }

  private createPeer(peerId: string): Peer {
    const pc = new RTCPeerConnection({
      iceServers: this.iceServers,
      bundlePolicy: "max-bundle",
      rtcpMuxPolicy: "require",
      iceCandidatePoolSize: 2,
    });

    const peer: Peer = {
      id: peerId,
      pc,
      transceivers: [],
      polite: this.selfPeerId < peerId,
      makingOffer: false,
      ignoreOffer: false,
      iceRestarted: false,
      reviving: false,
      tracks: { mic: null, cam: null, screen: null, screenAudio: null },
      streams: { mic: null, cam: null, screen: null },
      pending: this.early.get(peerId) ?? [],
      samples: new Map(),
    };
    this.early.delete(peerId);
    this.peers.set(peerId, peer);

    // Ordem fixa dos quatro transceivers: microfone, camera, tela, audio da
    // tela — mas so o lado IMPOLITE os cria antecipadamente. Se os dois lados
    // chamassem addTransceiver aqui, cada um atribuiria seus proprios `mid`s
    // antes de qualquer troca de SDP; quando a oferta de um lado chega no
    // outro, os mids nao batem e o navegador cria 4 transceivers NOVOS para
    // cobrir as m-lines recebidas, duplicando para 8 e deixando a trilha real
    // presa nos 4 originais (que so enviam, nunca mais recebem). O lado
    // educado, em vez disso, aguarda a oferta chegar e reaproveita os
    // transceivers que o proprio navegador cria ao processa-la —
    // ver `ensureTransceivers`.
    if (!peer.polite) {
      peer.transceivers.push(
        pc.addTransceiver(this.micTrack ?? "audio", { direction: "sendrecv" }),
      );
      peer.transceivers.push(
        pc.addTransceiver(this.camTrack ?? "video", {
          direction: "sendrecv",
          sendEncodings: [encodingFor(presetOf(this.camQuality))],
        }),
      );
      peer.transceivers.push(
        pc.addTransceiver(this.screenTrack ?? "video", {
          direction: "sendrecv",
          sendEncodings: [encodingFor(presetOf(this.shareQuality))],
        }),
      );
      peer.transceivers.push(
        pc.addTransceiver(this.screenAudioTrack ?? "audio", { direction: "sendrecv" }),
      );

      preferCodecs(peer.transceivers[SLOT_CAM]);
      preferCodecs(peer.transceivers[SLOT_SCREEN]);
    }

    pc.onnegotiationneeded = () => void this.negotiate(peer);
    pc.onicecandidate = event => {
      if (event.candidate) this.send(peer.id, { kind: "candidate", data: event.candidate.toJSON() });
    };
    pc.ontrack = event => this.handleTrack(peer, event);
    pc.onconnectionstatechange = () => this.handleConnectionState(peer);

    this.applyPeerEncodings(peer);
    return peer;
  }

  /**
   * Lado educado: os transceivers ainda nao existem localmente (nenhum
   * `addTransceiver` foi chamado em `createPeer`). Depois que a primeira
   * oferta do lado impolite e aplicada, o navegador ja criou os transceivers
   * correspondentes as m-lines recebidas — na mesma ordem fixa que o outro
   * lado usou para ofertar (mic, cam, tela, audio da tela), porque so ele
   * ofertou. Aqui so os "adotamos" e publicamos as trilhas locais que ja
   * estiverem disponiveis.
   */
  private ensureTransceivers(peer: Peer): void {
    if (peer.transceivers.length > 0) return;
    const found = peer.pc.getTransceivers();
    if (found.length < 4) return;
    peer.transceivers = found.slice(0, 4);
    preferCodecs(peer.transceivers[SLOT_CAM]);
    preferCodecs(peer.transceivers[SLOT_SCREEN]);
    void this.pushLocalTracksTo(peer);
  }

  /** Aplica as trilhas locais atuais aos quatro slots de um unico peer. */
  private async pushLocalTracksTo(peer: Peer): Promise<void> {
    // Transceiver auto-criado pelo navegador ao processar uma oferta remota
    // nasce com direction "recvonly", mesmo que a oferta tenha pedido
    // "sendrecv": o receptor so passa a enviar quando decide isso
    // explicitamente. Sem esta linha, o replaceTrack abaixo ate anexa a
    // trilha ao sender, mas o RTP nunca sai — a direcao continua proibindo o
    // envio, e o outro lado nunca recebe nada (mesmo a conexao dando
    // "connected" e a trilha aparecendo como "live" do lado receptor). Mudar
    // a direction dispara uma renegociacao que fecha o fluxo bidirecional.
    for (const transceiver of peer.transceivers) {
      if (transceiver && transceiver.direction !== "sendrecv") {
        transceiver.direction = "sendrecv";
      }
    }
    const jobs: Promise<void>[] = [
      peer.transceivers[SLOT_MIC]?.sender.replaceTrack(this.micTrack).catch(() => undefined),
      peer.transceivers[SLOT_CAM]?.sender.replaceTrack(this.camTrack).catch(() => undefined),
      peer.transceivers[SLOT_SCREEN]?.sender.replaceTrack(this.screenTrack).catch(() => undefined),
      peer.transceivers[SLOT_SCREEN_AUDIO]?.sender
        .replaceTrack(this.screenAudioTrack)
        .catch(() => undefined),
    ].filter((job): job is Promise<void> => job !== undefined);
    await Promise.all(jobs);
    this.applyPeerEncodings(peer);
  }

  private dropPeer(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    this.peers.delete(peerId);
    this.closeConnection(peer);
    this.detector.detach(peerId);
    if (this.destroyed) return;
    for (const kind of ["mic", "cam", "screen"] as MediaKind[]) {
      if (peer.streams[kind]) this.callbacks.onRemoteStream(peerId, kind, null);
    }
  }

  private closeConnection(peer: Peer): void {
    peer.pc.onnegotiationneeded = null;
    peer.pc.onicecandidate = null;
    peer.pc.ontrack = null;
    peer.pc.onconnectionstatechange = null;
    peer.pc.close();
  }

  /** Falha de rede: uma tentativa de `restartIce` e, se nao voltar, conexao nova. */
  private handleConnectionState(peer: Peer): void {
    if (this.destroyed) return;
    const state = peer.pc.connectionState;
    if (state === "connected") {
      peer.iceRestarted = false;
      this.applyPeerEncodings(peer);
      return;
    }
    if (state !== "failed") return;
    if (!peer.iceRestarted) {
      peer.iceRestarted = true;
      peer.pc.restartIce();
      return;
    }
    if (peer.reviving) return;
    peer.reviving = true;
    setTimeout(() => {
      if (this.destroyed || !this.wanted.has(peer.id)) return;
      if (this.peers.get(peer.id) !== peer) return;
      this.dropPeer(peer.id);
      this.createPeer(peer.id);
    }, REVIVE_DELAY_MS);
  }

  /* ------------------------------------------------------------------ */
  /* Negociacao perfeita                                                 */
  /* ------------------------------------------------------------------ */

  private async negotiate(peer: Peer): Promise<void> {
    if (this.destroyed) return;
    try {
      peer.makingOffer = true;
      await peer.pc.setLocalDescription();
      const description = peer.pc.localDescription;
      if (description) this.send(peer.id, { kind: "offer", data: this.describe(description) });
    } catch {
      // A proxima mudanca de trilha dispara nova negociacao.
    } finally {
      peer.makingOffer = false;
    }
  }

  handleSignal(from: string, signal: RtcSignal): void {
    void this.applySignal(from, signal);
  }

  private async applySignal(from: string, signal: RtcSignal): Promise<void> {
    if (this.destroyed || from === this.selfPeerId) return;

    let peer = this.peers.get(from);
    if (!peer) {
      if (signal.kind === "candidate") {
        const candidate = asCandidate(signal.data);
        if (!candidate) return;
        const queue = this.early.get(from) ?? [];
        if (queue.length < MAX_EARLY_CANDIDATES) queue.push(candidate);
        this.early.set(from, queue);
        return;
      }
      if (signal.kind !== "offer") return;
      this.wanted.add(from);
      peer = this.createPeer(from);
    }

    try {
      if (signal.kind === "candidate") {
        const candidate = asCandidate(signal.data);
        if (!candidate) return;
        if (!peer.pc.remoteDescription) {
          peer.pending.push(candidate);
          return;
        }
        await peer.pc.addIceCandidate(candidate);
        return;
      }

      const description = asDescription(signal.data);
      if (!description) return;

      const collision =
        description.type === "offer" && (peer.makingOffer || peer.pc.signalingState !== "stable");
      peer.ignoreOffer = !peer.polite && collision;
      if (peer.ignoreOffer) return;

      // Ajustar o SDP recebido e o que configura o **nosso** encoder: os
      // parametros fmtp do lado remoto definem o que enviamos para ele.
      const tuned: RTCSessionDescriptionInit = description.sdp
        ? { type: description.type, sdp: tuneSdp(description.sdp, this.tuning()) }
        : description;

      try {
        await peer.pc.setRemoteDescription(tuned);
      } catch (error) {
        // Se o navegador recusar o SDP ajustado, vale mais conectar sem o ajuste.
        if (tuned === description) throw error;
        await peer.pc.setRemoteDescription(description);
      }
      await this.drainCandidates(peer);
      this.ensureTransceivers(peer);

      if (description.type === "offer") {
        await peer.pc.setLocalDescription();
        const local = peer.pc.localDescription;
        if (local) this.send(peer.id, { kind: "answer", data: this.describe(local) });
      }
      this.applyPeerEncodings(peer);
    } catch {
      // Sinal fora de ordem ou candidato invalido: a malha se recupera sozinha.
    }
  }

  private async drainCandidates(peer: Peer): Promise<void> {
    const queue = peer.pending;
    peer.pending = [];
    for (const candidate of queue) {
      try {
        await peer.pc.addIceCandidate(candidate);
      } catch {
        // Candidato de uma sessao anterior; pode ser descartado.
      }
    }
  }

  private describe(description: RTCSessionDescription): RTCSessionDescriptionInit {
    return { type: description.type, sdp: tuneSdp(description.sdp, this.tuning()) };
  }

  private tuning(): SdpTuning {
    const cam = presetOf(this.camQuality);
    const screen = presetOf(this.shareQuality);
    return {
      startBitrateKbps: screen.startBitrateKbps,
      maxBitrateKbps: kbpsOf(screen.maxBitrate),
      minBitrateKbps: floorOf(screen.startBitrateKbps),
      opusBitrate: OPUS_MIC_BITRATE,
      stereo: false,
      videoBySection: [
        {
          startBitrateKbps: cam.startBitrateKbps,
          maxBitrateKbps: kbpsOf(cam.maxBitrate),
          minBitrateKbps: floorOf(cam.startBitrateKbps),
        },
        {
          startBitrateKbps: screen.startBitrateKbps,
          maxBitrateKbps: kbpsOf(screen.maxBitrate),
          minBitrateKbps: floorOf(screen.startBitrateKbps),
        },
      ],
      opusBySection: [
        { bitrate: OPUS_MIC_BITRATE, stereo: false },
        { bitrate: OPUS_SHARE_BITRATE, stereo: true },
      ],
    };
  }

  private send(to: string, signal: RtcSignal): void {
    if (this.destroyed) return;
    this.socket.emit("rtc:signal", { to, signal });
  }

  /* ------------------------------------------------------------------ */
  /* Trilhas remotas                                                     */
  /* ------------------------------------------------------------------ */

  private handleTrack(peer: Peer, event: RTCTrackEvent): void {
    if (this.destroyed) return;
    const slots = peer.pc.getTransceivers();
    const index = slots.indexOf(event.transceiver);
    const slot = index === -1 ? peer.transceivers.indexOf(event.transceiver) : index;
    const track = event.track;

    if (slot === SLOT_MIC) {
      // O microfone fica sempre publicado: mudo e apenas `enabled = false`,
      // o fluxo RTP continua e o elemento de audio nao pode ser recriado.
      peer.tracks.mic = track;
      track.addEventListener("ended", () => {
        peer.tracks.mic = null;
        this.emitRemote(peer, "mic");
      });
      this.emitRemote(peer, "mic");
      return;
    }
    if (slot === SLOT_CAM) {
      this.bindOptionalTrack(peer, track, "cam", "cam");
      return;
    }
    if (slot === SLOT_SCREEN) {
      this.bindOptionalTrack(peer, track, "screen", "screen");
      return;
    }
    if (slot === SLOT_SCREEN_AUDIO) {
      this.bindOptionalTrack(peer, track, "screenAudio", "screen");
    }
  }

  /**
   * Camera, tela e audio da tela entram e saem com `replaceTrack`, sem
   * renegociar: do lado remoto isso aparece como `mute`/`unmute` da trilha.
   */
  private bindOptionalTrack(
    peer: Peer,
    track: MediaStreamTrack,
    field: "cam" | "screen" | "screenAudio",
    kind: MediaKind,
  ): void {
    const update = () => {
      if (this.destroyed) return;
      const live = track.readyState === "live" && !track.muted;
      const next = live ? track : null;
      if (peer.tracks[field] === next) return;
      peer.tracks[field] = next;
      this.emitRemote(peer, kind);
    };
    track.addEventListener("mute", update);
    track.addEventListener("unmute", update);
    track.addEventListener("ended", update);
    update();
  }

  private emitRemote(peer: Peer, kind: MediaKind): void {
    if (this.destroyed) return;
    const wanted =
      kind === "mic"
        ? [peer.tracks.mic]
        : kind === "cam"
          ? [peer.tracks.cam]
          : [peer.tracks.screen, peer.tracks.screenAudio];
    const tracks = wanted.filter((track): track is MediaStreamTrack => track !== null);
    const current = peer.streams[kind];
    if (sameTracks(current, tracks)) return;

    const stream = tracks.length > 0 ? new MediaStream(tracks) : null;
    peer.streams[kind] = stream;
    if (kind === "mic") {
      if (stream) this.detector.attach(peer.id, stream);
      else this.detector.detach(peer.id);
    }
    this.callbacks.onRemoteStream(peer.id, kind, stream);
  }

  /* ------------------------------------------------------------------ */
  /* Trilhas locais                                                      */
  /* ------------------------------------------------------------------ */

  enableMic(): Promise<void> {
    if (this.destroyed || this.micTrack) return Promise.resolve();
    if (!this.micRequest) {
      this.micRequest = this.requestMic().finally(() => {
        this.micRequest = null;
      });
    }
    return this.micRequest;
  }

  private async requestMic(): Promise<void> {
    let capture: MicCapture;
    try {
      capture = await getMicStream(this.noiseSuppression, () => {
        /*
          O contexto de audio do microfone nao voltou. A trilha ainda parece
          viva, mas so carrega silencio — e sem este aviso ninguem descobriria
          ate alguem dizer "nao estou te ouvindo". Reabrir o microfone inteiro
          e a unica saida: e o mesmo caminho de trocar de dispositivo.
        */
        if (this.destroyed) return;
        this.callbacks.onError("O microfone parou de responder. Reabrindo…");
        void this.reabrirMicrofone();
      });
    } catch (error) {
      this.callbacks.onError(messageOf(error, "Não foi possível ligar o microfone."));
      return;
    }
    if (this.destroyed) {
      capture.dispose();
      return;
    }
    const track = capture.stream.getAudioTracks()[0];
    if (!track) {
      capture.dispose();
      this.callbacks.onError("O microfone não entregou nenhuma trilha de áudio.");
      return;
    }
    track.enabled = !this.muted;
    track.addEventListener("ended", () => {
      this.micTrack = null;
      this.micStream = null;
      this.micDispose?.();
      this.micDispose = null;
      void this.setSlotTrack(SLOT_MIC, null);
      this.detector.detach(this.selfPeerId);
      this.publishLocal();
      this.callbacks.onError("O microfone foi desconectado.");
    });

    this.micStream = capture.stream;
    this.micTrack = track;
    this.micDispose = capture.dispose;
    await this.setSlotTrack(SLOT_MIC, track);
    this.detector.attach(this.selfPeerId, capture.stream);
    this.publishLocal();
  }

  /**
   * Refaz o microfone do zero, preservando o estado de mudo.
   *
   * Solta a trilha atual antes: ela ainda esta `live` (por isso ninguem
   * percebeu que morreu), entao `enableMic` sozinho nao substituiria nada.
   * O `ended` manual e o mesmo aviso que a troca de dispositivo usa para a
   * malha largar a trilha antiga.
   */
  private async reabrirMicrofone(): Promise<void> {
    if (this.destroyed) return;
    const antiga = this.micTrack;
    if (antiga) {
      antiga.stop();
      antiga.dispatchEvent(new Event("ended"));
    }
    await this.enableMic();
    if (this.micTrack) this.micTrack.enabled = !this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.micTrack) this.micTrack.enabled = !muted;
  }

  /**
   * So guarda a preferencia — nao troca a trilha em uso. Quem chama decide
   * se reabre o microfone agora (a store usa o mesmo `restartMic` que ja
   * existe para trocar de dispositivo de entrada).
   */
  setNoiseSuppressionPreference(on: boolean): void {
    this.noiseSuppression = on;
  }

  async setCam(on: boolean): Promise<void> {
    if (this.destroyed) return;
    if (!on) {
      this.stopCam();
      await this.setSlotTrack(SLOT_CAM, null);
      this.publishLocal();
      return;
    }
    if (this.camTrack) return;

    let stream: MediaStream;
    try {
      stream = await getCamStream(this.camQuality);
    } catch (error) {
      const message = messageOf(error, "Não foi possível ligar a câmera.");
      this.callbacks.onError(message);
      throw new Error(message);
    }
    if (this.destroyed) {
      stopStream(stream);
      return;
    }
    const track = stream.getVideoTracks()[0];
    if (!track) {
      stopStream(stream);
      const message = "A câmera não entregou nenhuma trilha de vídeo.";
      this.callbacks.onError(message);
      throw new Error(message);
    }

    this.camStream = stream;
    this.camTrack = track;
    track.addEventListener("ended", () => {
      if (this.camTrack !== track) return;
      this.stopCam();
      void this.setSlotTrack(SLOT_CAM, null);
      this.publishLocal();
      this.callbacks.onCamEnded();
    });

    await this.setSlotTrack(SLOT_CAM, track);
    await this.applyEncoding(SLOT_CAM, presetOf(this.camQuality));
    this.publishLocal();
  }

  async setScreen(on: boolean, includeSystemAudio = false): Promise<void> {
    if (this.destroyed) return;
    if (!on) {
      this.stopScreen();
      await this.setSlotTrack(SLOT_SCREEN, null);
      await this.setSlotTrack(SLOT_SCREEN_AUDIO, null);
      this.publishLocal();
      return;
    }
    if (this.screenTrack) return;

    let captured: { stream: MediaStream; capture: CaptureResult };
    try {
      captured = await getScreenStream(this.shareQuality, this.contentHint, includeSystemAudio);
    } catch (error) {
      const message = messageOf(error, "Não foi possível iniciar a transmissão.");
      this.callbacks.onError(message);
      throw new Error(message);
    }
    const { stream, capture } = captured;
    if (this.destroyed) {
      stopStream(stream);
      return;
    }
    const video = stream.getVideoTracks()[0];
    if (!video) {
      stopStream(stream);
      const message = "A transmissão não entregou nenhuma trilha de vídeo.";
      this.callbacks.onError(message);
      throw new Error(message);
    }
    const audio = stream.getAudioTracks()[0] ?? null;

    this.screenStream = stream;
    this.screenTrack = video;
    this.screenAudioTrack = audio;
    video.addEventListener("ended", () => {
      if (this.screenTrack !== video) return;
      this.stopScreen();
      void this.setSlotTrack(SLOT_SCREEN, null);
      void this.setSlotTrack(SLOT_SCREEN_AUDIO, null);
      this.publishLocal();
      this.callbacks.onShareEnded();
    });

    await this.setSlotTrack(SLOT_SCREEN, video);
    await this.setSlotTrack(SLOT_SCREEN_AUDIO, audio);
    await this.applyEncoding(SLOT_SCREEN, presetOf(this.shareQuality));
    this.publishLocal();
    this.callbacks.onShareStarted(capture);
  }

  async setShareQuality(quality: QualityId): Promise<void> {
    this.shareQuality = quality;
    if (this.destroyed) return;
    const preset = presetOf(quality);
    if (this.screenTrack) {
      const ok = await applyVideoQuality(this.screenTrack, preset);
      if (!ok) this.callbacks.onError(`A fonte capturada não aceita ${preset.label}.`);
    }
    await this.applyEncoding(SLOT_SCREEN, preset);
  }

  async setCamQuality(quality: QualityId): Promise<void> {
    this.camQuality = quality;
    if (this.destroyed) return;
    const preset = presetOf(quality);
    if (this.camTrack) {
      const ok = await applyVideoQuality(this.camTrack, preset);
      if (!ok) this.callbacks.onError(`A câmera não aceita ${preset.label}.`);
    }
    await this.applyEncoding(SLOT_CAM, preset);
  }

  setContentHint(mode: ContentHintMode): void {
    this.contentHint = mode;
    if (this.screenTrack) this.screenTrack.contentHint = mode;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    clearInterval(this.statsTimer);
    for (const peer of this.peers.values()) this.closeConnection(peer);
    this.peers.clear();
    this.early.clear();
    this.wanted.clear();
    this.detector.destroy();
    this.stopScreen();
    this.stopCam();
    this.stopMic();
  }

  private stopMic(): void {
    this.micDispose?.();
    this.micDispose = null;
    stopStream(this.micStream);
    this.micStream = null;
    this.micTrack = null;
  }

  private stopCam(): void {
    stopStream(this.camStream);
    this.camStream = null;
    this.camTrack = null;
  }

  private stopScreen(): void {
    stopStream(this.screenStream);
    this.screenStream = null;
    this.screenTrack = null;
    this.screenAudioTrack = null;
  }

  private publishLocal(): void {
    if (this.destroyed) return;
    this.callbacks.onLocalStreams({
      mic: this.micStream,
      cam: this.camStream,
      screen: this.screenStream,
    });
  }

  private async setSlotTrack(slot: number, track: MediaStreamTrack | null): Promise<void> {
    const jobs: Promise<unknown>[] = [];
    for (const peer of this.peers.values()) {
      const sender = peer.transceivers[slot]?.sender;
      if (!sender) continue;
      jobs.push(sender.replaceTrack(track).catch(() => undefined));
    }
    await Promise.all(jobs);
  }

  private async applyEncoding(slot: number, preset: QualityPreset): Promise<void> {
    const jobs: Promise<unknown>[] = [];
    for (const peer of this.peers.values()) {
      const sender = peer.transceivers[slot]?.sender;
      if (sender) jobs.push(tuneSender(sender, preset));
    }
    await Promise.all(jobs);
  }

  private applyPeerEncodings(peer: Peer): void {
    void tuneSender(peer.transceivers[SLOT_CAM]?.sender, presetOf(this.camQuality));
    void tuneSender(peer.transceivers[SLOT_SCREEN]?.sender, presetOf(this.shareQuality));
  }

  /* ------------------------------------------------------------------ */
  /* Estatisticas                                                        */
  /* ------------------------------------------------------------------ */

  private async collectStats(): Promise<void> {
    if (this.destroyed || this.peers.size === 0) return;
    const report: Record<string, Partial<Record<MediaKind, PeerStats>>> = {};

    for (const peer of this.peers.values()) {
      const entry: Partial<Record<MediaKind, PeerStats>> = {};
      for (const [kind, slot] of RECEIVE_SLOTS) {
        const stats = await this.readInbound(peer, slot, kind);
        if (stats) entry[kind] = stats;
      }
      if (Object.keys(entry).length > 0) report[peer.id] = entry;
    }

    // O que estamos enviando: basta olhar uma conexao, o encoder e o mesmo.
    const primary = [...this.peers.values()][0];
    if (primary) {
      const local: Partial<Record<MediaKind, PeerStats>> = {};
      for (const [kind, slot] of RECEIVE_SLOTS) {
        const stats = await this.readOutbound(primary, slot, kind);
        if (stats) local[kind] = stats;
      }
      if (Object.keys(local).length > 0) report[this.selfPeerId] = local;
    }

    if (!this.destroyed) this.callbacks.onStats(report);
  }

  private async readInbound(peer: Peer, slot: number, kind: MediaKind): Promise<PeerStats | null> {
    const receiver = peer.transceivers[slot]?.receiver;
    if (!receiver) return null;
    let stats: Map<string, StatRecord>;
    try {
      stats = indexStats(await receiver.getStats());
    } catch {
      return null;
    }
    const inbound = findByType(stats, "inbound-rtp");
    if (!inbound) return null;
    const bytes = numberOf(inbound, "bytesReceived") ?? 0;
    if (bytes <= 0) return null;
    const remote = findByType(stats, "remote-outbound-rtp");
    return {
      width: numberOf(inbound, "frameWidth") ?? 0,
      height: numberOf(inbound, "frameHeight") ?? 0,
      frameRate: Math.round(numberOf(inbound, "framesPerSecond") ?? 0),
      bitrateKbps: this.rateOf(peer, `in:${kind}`, bytes, numberOf(inbound, "timestamp")),
      codec: codecOf(stats, inbound),
      packetsLost: numberOf(inbound, "packetsLost") ?? 0,
      rttMs: Math.round((numberOf(remote, "roundTripTime") ?? 0) * 1000),
    };
  }

  private async readOutbound(peer: Peer, slot: number, kind: MediaKind): Promise<PeerStats | null> {
    const sender = peer.transceivers[slot]?.sender;
    if (!sender || !sender.track) return null;
    let stats: Map<string, StatRecord>;
    try {
      stats = indexStats(await sender.getStats());
    } catch {
      return null;
    }
    const outbound = findByType(stats, "outbound-rtp");
    if (!outbound) return null;
    const bytes = numberOf(outbound, "bytesSent") ?? 0;
    if (bytes <= 0) return null;
    const remote = findByType(stats, "remote-inbound-rtp");
    return {
      width: numberOf(outbound, "frameWidth") ?? 0,
      height: numberOf(outbound, "frameHeight") ?? 0,
      frameRate: Math.round(numberOf(outbound, "framesPerSecond") ?? 0),
      bitrateKbps: this.rateOf(peer, `out:${kind}`, bytes, numberOf(outbound, "timestamp")),
      codec: codecOf(stats, outbound),
      packetsLost: numberOf(remote, "packetsLost") ?? 0,
      rttMs: Math.round((numberOf(remote, "roundTripTime") ?? 0) * 1000),
    };
  }

  /** Bits por milissegundo = kbps; guarda a amostra anterior por peer e trilha. */
  private rateOf(peer: Peer, key: string, bytes: number, timestamp: number | null): number {
    const at = timestamp ?? Date.now();
    const previous = peer.samples.get(key);
    peer.samples.set(key, { bytes, at });
    if (!previous || at <= previous.at) return 0;
    const bits = Math.max(0, bytes - previous.bytes) * 8;
    return Math.round(bits / (at - previous.at));
  }
}

/* -------------------------------------------------------------------- */
/* Auxiliares                                                            */
/* -------------------------------------------------------------------- */

/**
 * `scaleResolutionDownBy: 1` e `maintain-framerate` sao o par que segura os
 * 60 fps: sem eles o Chrome derruba quadros antes de derrubar resolucao.
 */
function encodingFor(preset: QualityPreset): RTCRtpEncodingParameters {
  return {
    active: true,
    scaleResolutionDownBy: 1,
    networkPriority: "high",
    priority: "high",
    maxBitrate: preset.maxBitrate ?? undefined,
    maxFramerate: preset.frameRate ?? undefined,
  };
}

async function tuneSender(
  sender: RTCRtpSender | undefined,
  preset: QualityPreset,
): Promise<void> {
  if (!sender) return;
  try {
    const params = sender.getParameters();
    const current = Array.isArray(params.encodings) ? params.encodings : [];
    const base = current.length > 0 ? current[0] : {};
    params.encodings = [{ ...base, ...encodingFor(preset) }];
    params.degradationPreference = "maintain-framerate";
    await sender.setParameters(params);
  } catch {
    // O navegador recusa setParameters entre negociacoes; reaplicamos depois.
  }
}

function kbpsOf(bitrate: number | null): number | null {
  return bitrate === null ? null : Math.round(bitrate / 1000);
}

/** Piso do encoder: um quarto do bitrate inicial evita o modo "borrão". */
function floorOf(startKbps: number | null): number | null {
  return startKbps === null ? null : Math.max(300, Math.round(startKbps / 4));
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function sameTracks(stream: MediaStream | null, tracks: MediaStreamTrack[]): boolean {
  if (!stream) return tracks.length === 0;
  const current = stream.getTracks();
  if (current.length !== tracks.length) return false;
  return current.every((track, index) => track === tracks[index]);
}

function asDescription(data: unknown): RTCSessionDescriptionInit | null {
  if (typeof data !== "object" || data === null) return null;
  const record = data as { type?: unknown; sdp?: unknown };
  const type = record.type;
  if (type !== "offer" && type !== "answer" && type !== "pranswer" && type !== "rollback") {
    return null;
  }
  if (record.sdp !== undefined && typeof record.sdp !== "string") return null;
  return { type, sdp: typeof record.sdp === "string" ? record.sdp : undefined };
}

function asCandidate(data: unknown): RTCIceCandidateInit | null {
  if (typeof data !== "object" || data === null) return null;
  const record = data as {
    candidate?: unknown;
    sdpMid?: unknown;
    sdpMLineIndex?: unknown;
    usernameFragment?: unknown;
  };
  if (typeof record.candidate !== "string") return null;
  return {
    candidate: record.candidate,
    sdpMid: typeof record.sdpMid === "string" ? record.sdpMid : null,
    sdpMLineIndex: typeof record.sdpMLineIndex === "number" ? record.sdpMLineIndex : null,
    usernameFragment:
      typeof record.usernameFragment === "string" ? record.usernameFragment : null,
  };
}

function indexStats(report: RTCStatsReport): Map<string, StatRecord> {
  const stats = new Map<string, StatRecord>();
  report.forEach((value: unknown, key: string) => {
    if (typeof value === "object" && value !== null) stats.set(key, value as StatRecord);
  });
  return stats;
}

function findByType(stats: Map<string, StatRecord>, type: string): StatRecord | null {
  for (const record of stats.values()) {
    if (record.type === type) return record;
  }
  return null;
}

function numberOf(record: StatRecord | null, key: string): number | null {
  if (!record) return null;
  const value = record[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function codecOf(stats: Map<string, StatRecord>, record: StatRecord): string {
  const codecId = record.codecId;
  if (typeof codecId !== "string") return "";
  const codec = stats.get(codecId);
  const mimeType = codec?.mimeType;
  if (typeof mimeType !== "string") return "";
  const slash = mimeType.indexOf("/");
  return slash === -1 ? mimeType : mimeType.slice(slash + 1);
}
